import { differenceInMinutes, parse } from 'date-fns';

/**
 * Calculate the difference in shift duration when adjustments are made.
 * Returns a formatted string showing the time difference.
 *
 * @param originalStartTime - Original shift start time (e.g., "08:00 am")
 * @param originalEndTime - Original shift end time (e.g., "05:00 pm")
 * @param adjustedStartTime - Adjusted shift start time (e.g., "08:30 am")
 * @param adjustedEndTime - Adjusted shift end time (e.g., "05:30 pm")
 * @returns Formatted time difference (e.g., "+30 min", "-1 hr", "+1 hr 15 min")
 *
 * @example
 * calculateAdjustedShiftTime("08:00 am", "05:00 pm", "08:00 am", "05:30 pm")
 * // Returns: "+30 min"
 *
 * @example
 * calculateAdjustedShiftTime("08:00 am", "05:00 pm", "09:00 am", "05:00 pm")
 * // Returns: "-1 hr"
 */
export const calculateAdjustedShiftTime = (
  originalStartTime: string,
  originalEndTime: string,
  adjustedStartTime?: string,
  adjustedEndTime?: string,
): string => {
  if (!adjustedStartTime || !adjustedEndTime) {
    return '';
  }

  const timeFormat = 'hh:mm aaa';
  const referenceDate = new Date(2000, 0, 1);

  // Parse original times
  const originalStart = parse(originalStartTime, timeFormat, referenceDate);
  let originalEnd = parse(originalEndTime, timeFormat, referenceDate);

  // Handle midnight crossing for original shift
  if (originalEnd < originalStart) {
    originalEnd = new Date(originalEnd.getTime() + 24 * 60 * 60 * 1000);
  }
  const originalDuration = differenceInMinutes(originalEnd, originalStart);

  // Parse adjusted times
  const adjustedStart = parse(adjustedStartTime, timeFormat, referenceDate);
  let adjustedEnd = parse(adjustedEndTime, timeFormat, referenceDate);

  // Handle midnight crossing for adjusted shift
  if (adjustedEnd < adjustedStart) {
    adjustedEnd = new Date(adjustedEnd.getTime() + 24 * 60 * 60 * 1000);
  }
  const adjustedDuration = differenceInMinutes(adjustedEnd, adjustedStart);

  // Calculate difference (positive = longer shift, negative = shorter shift)
  const difference = adjustedDuration - originalDuration;

  return formatTime(difference);
};

/**
 * Calculate the difference in break time when adjustments are made.
 * Returns a formatted string showing the break time difference.
 *
 * @param originalBreakTime - Original break time in minutes as string (e.g., "30")
 * @param adjustedBreakTime - Adjusted break time in minutes as string (e.g., "60")
 * @returns Formatted time difference (e.g., "+30 min", "-1 hr")
 *
 * @example
 * calculateAdjustedBreakTime("30", "60")
 * // Returns: "+30 min"
 *
 * @example
 * calculateAdjustedBreakTime("60", "30")
 * // Returns: "-30 min"
 */
export const calculateAdjustedBreakTime = (
  originalBreakTime: string,
  adjustedBreakTime?: string,
): string => {
  if (!adjustedBreakTime) {
    return '';
  }

  const originalMinutes = parseInt(originalBreakTime, 10);
  const adjustedMinutes = parseInt(adjustedBreakTime, 10);

  // Calculate difference (positive = longer break, negative = shorter break)
  const difference = adjustedMinutes - originalMinutes;

  return formatTime(difference);
};

/**
 * Format a time in minutes to a human-readable string.
 *
 * @param minutes - Time in minutes (can be positive or negative)
 * @param showSign - Whether to show the sign (e.g., "+" or "-")
 * @returns Formatted string (e.g., "+30 min", "-1 hr", "+1 hr 15 min", "0 min")
 *
 */
export const formatTime = (minutes: number, showSign = true): string => {
  if (minutes === 0) {
    return '0 min';
  }

  const isPositive = minutes > 0;
  const absMinutes = Math.abs(minutes);
  const hours = Math.floor(absMinutes / 60);
  const remainingMinutes = absMinutes % 60;

  const sign = isPositive ? '+' : '-';

  // Only hours (e.g., "+1 hr", "-2 hr", "0 hr")
  if (remainingMinutes === 0) {
    return `${showSign ? sign : ''}${hours} hr`;
  }

  // Only minutes (e.g., "+30 min", "-15 min", "0 min")
  if (hours === 0) {
    return `${showSign ? sign : ''}${remainingMinutes} min`;
  }

  // Hours and minutes (e.g., "+1 hr 15 min", "-2 hr 30 min", "0 hr 30 min")
  return `${showSign ? sign : ''}${hours} hr ${remainingMinutes} min`;
};
